// A grammar term's explanation, as the margin shows it (PC lane B / side panel, the phone's term
// sheet; margin/hosts.tsx PinnedContent, a lazy chunk). The title (the term's name) is the host's.
// Order: what it is, the Finnish comparison, more; the name in the other language; related terms
// (each opens in place, the anchor stays the term in the page); the Kielioppi topic that explains it.
import { Fragment } from 'react';
import { Link } from 'react-router';
import { grammarHref, topicById } from '../index';
import { useLang, useT } from '../../i18n';
import { useMargin } from '../../margin/context';
import { termById, termText } from './index';
import { TermExamples } from './TermExamples';
import { TermMarkup } from './markup';
import './terms.css';

export function TermCard({ id }: { id: string }) {
  const t = useT();
  const T = t.grammarTerms;
  const lang = useLang();
  const { actions, anchor } = useMargin();
  const def = termById(id);
  if (!def) return <p className="empty-note">{T.unknown}</p>;
  const tx = termText(def, lang);
  const otherLang = lang === 'fi' ? 'en' : 'fi';
  const other = termText(def, otherLang);
  const related = (def.related ?? []).filter((r) => r !== id && termById(r));
  const topic = def.firstIn ? topicById(def.firstIn) : undefined;
  return (
    <div className="term-card" data-term-card={id}>
      <p className="term-card-short">
        <TermMarkup text={tx.short} />
      </p>
      {tx.compare ? (
        <p className="term-card-compare">
          <TermMarkup text={tx.compare} />
        </p>
      ) : null}
      {tx.long ? (
        <p className="term-card-long">
          <TermMarkup text={tx.long} />
        </p>
      ) : null}
      <TermExamples id={id} />
      <p className="term-card-other">
        <span className="term-card-l">{T.otherLang}:</span> <span lang={otherLang}>{other.name}</span>
      </p>
      {related.length ? (
        <p className="term-card-related">
          <span className="term-card-l">{T.related}:</span>{' '}
          {related.map((r, i) => (
            <Fragment key={r}>
              {i ? <span aria-hidden="true"> · </span> : null}
              <button type="button" className="term-card-rel" onClick={() => actions?.openTerm(r, anchor)}>
                {termText(termById(r)!, lang).name}
              </button>
            </Fragment>
          ))}
        </p>
      ) : null}
      {topic ? (
        <p className="term-card-topic">
          <Link to={grammarHref(topic.topic.id)} className="lesson-xref">
            {T.readMore}: {lang === 'fi' ? topic.topic.titleFi : topic.topic.titleEn}
          </Link>
        </p>
      ) : null}
    </div>
  );
}
